import { Controller } from "@hotwired/stimulus"

// Admin session countdown shown in the sidebar footer.
//
// The session is a fixed 24h window (no sliding), so for most of its life the
// remaining time is hours. We render "23h 59m" above 1h and "Xm YYs" below it,
// and tick coarsely (every 30s) while far out, switching to 1s in the final
// minute. When the session expires we flip to the expired state and redirect.
const HOUR_MS = 3600 * 1000
const COARSE_TICK_MS = 30 * 1000
const FINE_TICK_MS = 1000

export default class extends Controller {
  static values = { expiresAt: Number, loginPath: String }
  static targets = ["display"]

  connect() {
    if (!this.expiresAtValue) return
    this.currentTickMs = null
    this.update()
  }

  disconnect() {
    if (this.timer) clearInterval(this.timer)
  }

  // (Re)arm the interval only when the desired cadence changes, so we don't
  // recreate the timer on every tick.
  scheduleTick(tickMs) {
    if (this.currentTickMs === tickMs) return
    this.currentTickMs = tickMs
    if (this.timer) clearInterval(this.timer)
    this.timer = setInterval(() => this.update(), tickMs)
  }

  update() {
    const remainingMs = this.expiresAtValue - Date.now()

    if (remainingMs <= 0) {
      if (this.timer) clearInterval(this.timer)
      if (this.hasDisplayTarget) this.displayTarget.textContent = "expirada"
      this.element.dataset.state = "expired"
      // Wait 2s before redirecting so the user sees the status change.
      setTimeout(() => {
        if (this.loginPathValue) window.location.href = this.loginPathValue
      }, 2000)
      return
    }

    const totalSec = Math.floor(remainingMs / 1000)
    const hours = Math.floor(totalSec / 3600)
    const min = Math.floor((totalSec % 3600) / 60)
    const sec = totalSec % 60

    let label
    if (hours > 0) {
      label = `${hours}h ${String(min).padStart(2, "0")}m`
    } else if (min > 0) {
      label = `${min}m ${String(sec).padStart(2, "0")}s`
    } else {
      label = `${sec}s`
    }

    if (this.hasDisplayTarget) this.displayTarget.textContent = label

    // Visual state: ok / warning (≤2min) / critical (≤30s).
    if (totalSec <= 30) {
      this.element.dataset.state = "critical"
    } else if (totalSec <= 120) {
      this.element.dataset.state = "warning"
    } else {
      this.element.dataset.state = "ok"
    }

    // Adaptive cadence: coarse while above 1h, fine within the last hour so the
    // seconds tick visibly when it matters.
    this.scheduleTick(remainingMs > HOUR_MS ? COARSE_TICK_MS : FINE_TICK_MS)
  }
}
